import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";

const ContactPage = () => {
  React.useEffect(() => { document.title = "Contact Us | New Leaf PMC LLC"; }, []);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, formData);
      toast.success("Message sent successfully! We will contact you soon.");
      setFormData({ name: "", phone: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="contact-page">
      {/* Header */}
      <section className="py-12 md:py-16 bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-[#A0AAB2] text-lg max-w-2xl mx-auto">
            Have a question or ready to start your project? Get in touch with us today.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-6">Get In Touch</h2>
                
                <div className="space-y-5">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#1F2027] flex items-center justify-center flex-shrink-0">
                      <Phone size={22} className="text-[#FF6B35]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Phone</p>
                      <a href="tel:863-340-9244" className="text-[#A0AAB2] hover:text-[#FF6B35] transition-colors block">
                        863-340-9244
                      </a>
                      <a href="tel:863-340-8262" className="text-[#A0AAB2] hover:text-[#FF6B35] transition-colors block">
                        863-340-8262
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#1F2027] flex items-center justify-center flex-shrink-0">
                      <Mail size={22} className="text-[#FF6B35]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Email</p>
                      <a href="mailto:newleafpmcllc@gmail.com" className="text-[#A0AAB2] hover:text-[#FF6B35] transition-colors">
                        newleafpmcllc@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#1F2027] flex items-center justify-center flex-shrink-0">
                      <MapPin size={22} className="text-[#FF6B35]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Location</p>
                      <p className="text-[#A0AAB2]">
                        Lakeland, Florida 33805<br />
                        Serving Central Florida
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#1F2027] flex items-center justify-center flex-shrink-0">
                      <Clock size={22} className="text-[#FF6B35]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Hours</p>
                      <p className="text-[#A0AAB2]">
                        Monday - Saturday<br />
                        Contact for availability
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl overflow-hidden h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d112449.65605373847!2d-82.06731897089845!3d28.039465000000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88dd39e6c1e0c791%3A0x99dd2cc3fb5efe0!2sLakeland%2C%20FL!5e0!3m2!1sen!2sus!4v1674746400000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="New Leaf PMC Location"
                  data-testid="google-map"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Your Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                    placeholder="John Doe"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                    placeholder="(555) 123-4567"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                    placeholder="john@example.com"
                    data-testid="contact-form-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">Message *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    rows={5}
                    className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35] resize-none"
                    placeholder="Tell us about your project or question..."
                    data-testid="contact-form-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full gradient-btn py-4 rounded-lg font-bold text-lg flex items-center justify-center space-x-2 disabled:opacity-50"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
